/**
 * AuthListDetailPage — full-page entry manager for a single auth list.
 *
 * URL: /auth-lists/:id  (deep-linkable; replaces the old modal popup).
 *
 * Capabilities:
 *   - Search (substring on label / e164, debounced 250ms, server-side)
 *   - Pagination (offset-based, jump-to-page, configurable page size)
 *   - Inline `active` toggle (instant PATCH, optimistic)
 *   - Per-entry start/end validity window (datetime-local, optional)
 *   - Bulk select via checkboxes — toolbar shows Activate / Deactivate /
 *     Delete when N selected
 *   - CSV export of the current list
 *   - CSV bulk-import with per-row error reporting
 *   - Sample CSV download (template for non-technical users)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authLists, formatListRef } from '@/api/authLists';
import type { AuthListKind, AuthListEntryRow, BulkImportResult } from '@/api/authLists';
import { Textarea } from '@/components/ui/textarea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { DateTimePicker } from '@/components/DateTimePicker';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft, Plus, Trash2, Pencil, Search, Upload, Download, FileText,
  CheckCircle2, XCircle, MoreHorizontal, Key, Phone, X, ChevronLeft, ChevronRight,
  Clock,
} from 'lucide-react';
import { PhoneInput } from '@/components/PhoneInput';
import { KindBadge } from './AuthListsPage';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];
// Lenient sanity check on the COMBINED value <PhoneInput> emits
// (prefix + national digits). The backend accepts national input and
// normalizes to E.164 server-side — this only catches obviously-broken
// output (too short / too long) before the round-trip.
const E164_RE = /^\+[1-9]\d{7,14}$/;
const PIN_RE = /^\d{3,8}$/;
const PAGE_SIZES = [25, 50, 100, 200] as const;

// ── Top-level page ────────────────────────────────────────────────────────────

export function AuthListDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const canMutate = CAN_MUTATE.includes(user.role);

  const { data, isLoading, error } = useQuery({
    queryKey: ['authList', id],
    queryFn: () => authLists.get(id!),
    enabled: Boolean(id),
  });

  if (!id) {
    return <div className="text-sm text-red-500">Missing list id in URL.</div>;
  }
  if (isLoading) {
    return <div className="text-sm text-slate-400">Loading list…</div>;
  }
  if (error || !data?.list) {
    return (
      <div className="text-sm text-red-500">
        Could not load list. <Link to="/auth-lists" className="underline">Back to all lists</Link>
      </div>
    );
  }

  return (
    <AuthListDetailInner
      listId={id}
      list={data.list}
      canMutate={canMutate}
    />
  );
}

// ── Inner (loaded) component ──────────────────────────────────────────────────

function AuthListDetailInner({
  listId, list, canMutate,
}: {
  listId: string;
  list: import('@/api/authLists').AuthListRow;
  canMutate: boolean;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<typeof PAGE_SIZES[number]>(50);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<AuthListEntryRow | null>(null);
  const [deleting, setDeleting] = useState<AuthListEntryRow | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // Debounce the search box (250ms) so we don't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(0); // reset to first page when search changes
      setSelectedIds(new Set());
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ── Entries query ────────────────────────────────────────────────────────
  const entriesQuery = useQuery({
    queryKey: ['authListEntries', listId, debouncedSearch, page, pageSize],
    queryFn: () => authLists.listEntries(listId, {
      limit: pageSize,
      offset: page * pageSize,
      search: debouncedSearch || undefined,
    }),
  });
  const rows = useMemo(() => entriesQuery.data?.rows ?? [], [entriesQuery.data]);
  const total = entriesQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // ── Selection helpers ────────────────────────────────────────────────────
  const allSelectedOnPage = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someSelectedOnPage = rows.some((r) => selectedIds.has(r.id));
  const toggleAllOnPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) rows.forEach((r) => next.add(r.id));
      else rows.forEach((r) => next.delete(r.id));
      return next;
    });
  };
  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  // ── Mutations ────────────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['authListEntries', listId] });
    qc.invalidateQueries({ queryKey: ['authList', listId] });
    qc.invalidateQueries({ queryKey: ['authLists'] });
  };

  const togglePatch = useMutation({
    mutationFn: ({ entryId, active }: { entryId: string; active: boolean }) =>
      authLists.updateEntry(listId, entryId, { active }),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => {
      toast({
        title: 'Could not update entry',
        description: err instanceof Error ? err.message : 'unknown_error',
        variant: 'destructive',
      });
    },
  });

  const remove = useMutation({
    mutationFn: (entryId: string) => authLists.removeEntry(listId, entryId),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
    },
  });

  const bulkActive = useMutation({
    mutationFn: (active: boolean) =>
      authLists.bulkUpdateEntries(listId, {
        ids: Array.from(selectedIds),
        patch: { active },
      }),
    onSuccess: (data) => {
      toast({ title: `${data.updated} entr${data.updated === 1 ? 'y' : 'ies'} updated` });
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const bulkDelete = useMutation({
    mutationFn: () =>
      authLists.bulkDeleteEntries(listId, Array.from(selectedIds)),
    onSuccess: (data) => {
      toast({ title: `${data.deleted} entr${data.deleted === 1 ? 'y' : 'ies'} removed` });
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      invalidate();
    },
  });

  // ── Render ───────────────────────────────────────────────────────────────
  const selectionCount = selectedIds.size;
  const showingFrom = total === 0 ? 0 : page * pageSize + 1;
  const showingTo = Math.min((page + 1) * pageSize, total);

  return (
    <div className="space-y-4">
      {/* Top header: back link + title row */}
      <div className="space-y-2">
        <Link
          to="/auth-lists"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          <ArrowLeft className="h-3 w-3" /> All auth lists
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-slate-400">
                {formatListRef(list.ref_number)}
              </span>
              <h1 className="text-xl font-semibold tracking-tight">{list.name}</h1>
              <KindBadge kind={list.kind} />
              <span className="font-mono text-xs text-slate-400">
                · {total} entr{total === 1 ? 'y' : 'ies'}
              </span>
            </div>
            {list.description && (
              <p className="text-sm text-muted-foreground">{list.description}</p>
            )}
          </div>
          {canMutate && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/auth-lists', { state: { editId: list.id } })}
                title="Edit list metadata"
              >
                <Pencil className="h-3.5 w-3.5 me-1.5" /> Edit list
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Toolbar: search + bulk actions + import/export + add */}
      <div className="flex items-center gap-2 flex-wrap rounded-md border border-border bg-card p-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by phone or name…"
            className="pl-8 h-9 text-sm"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Bulk-action menu, only when something is selected */}
        {canMutate && selectionCount > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {selectionCount} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkActive.mutate(true)}
              disabled={bulkActive.isPending}
            >
              <CheckCircle2 className="h-3.5 w-3.5 me-1.5 text-emerald-600" />
              Activate
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => bulkActive.mutate(false)}
              disabled={bulkActive.isPending}
            >
              <XCircle className="h-3.5 w-3.5 me-1.5 text-amber-600" />
              Deactivate
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-destructive border-red-200 hover:bg-red-50 dark:border-red-900/50 dark:hover:bg-red-950/40"
              onClick={() => setBulkDeleteOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5 me-1.5" />
              Delete
            </Button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
            >
              Clear
            </button>
          </div>
        )}

        {/* Right-side action cluster — always visible */}
        <div className="ml-auto flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                <FileText className="h-3.5 w-3.5 me-1.5" />
                CSV
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canMutate && (
                <DropdownMenuItem onClick={() => setImportOpen(true)}>
                  <Upload className="h-3.5 w-3.5 me-2" /> Import from CSV…
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => {
                  // Use the resolved UUID (list.id) so the request hits the
                  // canonical endpoint. The download helper attaches the
                  // Bearer token — plain <a download> can't.
                  authLists.downloadEntriesCsv(list.id, `${list.name}-entries.csv`)
                    .catch((err) => toast({
                      title: 'Could not export CSV',
                      description: err instanceof Error ? err.message : 'unknown_error',
                      variant: 'destructive',
                    }));
                }}
              >
                <Download className="h-3.5 w-3.5 me-2" /> Export this list
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  authLists.downloadSampleCsv()
                    .catch((err) => toast({
                      title: 'Could not download sample',
                      description: err instanceof Error ? err.message : 'unknown_error',
                      variant: 'destructive',
                    }));
                }}
              >
                <FileText className="h-3.5 w-3.5 me-2" /> Download sample template
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {canMutate && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="h-3.5 w-3.5 me-1.5" />
              Add entry
            </Button>
          )}
        </div>
      </div>

      {/* Entries table */}
      <div className="rounded-md border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-900/40 border-b border-border">
            <tr className="text-xs text-muted-foreground">
              <th className="w-10 px-3 py-2">
                <Checkbox
                  checked={allSelectedOnPage}
                  onCheckedChange={(v) => toggleAllOnPage(Boolean(v))}
                  aria-label="Select all on page"
                  // Show indeterminate when some-but-not-all rows are selected.
                  // Radix doesn't expose a prop for this; we set data-state ourselves.
                  data-state={!allSelectedOnPage && someSelectedOnPage ? 'indeterminate' : undefined}
                />
              </th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Phone (E.164)</th>
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-left font-medium">Description</th>
              <th className="px-3 py-2 text-left font-medium">PIN</th>
              <th className="px-3 py-2 text-left font-medium">Active</th>
              <th className="px-3 py-2 text-left font-medium">Start</th>
              <th className="px-3 py-2 text-left font-medium">End</th>
              <th className="w-10 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {entriesQuery.isLoading ? (
              <tr><td colSpan={10} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-sm text-muted-foreground">
                  {debouncedSearch
                    ? `No entries match "${debouncedSearch}".`
                    : 'No entries yet. Click "Add entry" or import from CSV.'}
                </td>
              </tr>
            ) : rows.map((e) => (
              <EntryRow
                key={e.id}
                entry={e}
                listKind={list.kind}
                checked={selectedIds.has(e.id)}
                onCheckedChange={(v) => toggleOne(e.id, v)}
                onToggleActive={(v) => togglePatch.mutate({ entryId: e.id, active: v })}
                onEdit={() => setEditing(e)}
                onDelete={() => setDeleting(e)}
                canMutate={canMutate}
                pendingActive={togglePatch.isPending && togglePatch.variables?.entryId === e.id}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between gap-3 flex-wrap text-xs">
        <div className="text-muted-foreground">
          {total === 0
            ? 'No entries.'
            : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value) as typeof PAGE_SIZES[number]); setPage(0); }}
            className="rounded border border-input bg-background px-2 py-1 text-xs"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <Button
            size="sm"
            variant="outline"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="font-mono">
            Page {page + 1} of {pageCount}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Dialogs */}
      {addOpen && (
        <EntryFormDialog
          mode="create"
          listId={listId}
          listKind={list.kind}
          customerId={list.customer_id}
          onClose={() => setAddOpen(false)}
        />
      )}
      {editing && (
        <EntryFormDialog
          mode="edit"
          listId={listId}
          listKind={list.kind}
          customerId={list.customer_id}
          entry={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <ConfirmRemoveDialog
          entry={deleting}
          onCancel={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          busy={remove.isPending}
        />
      )}
      {bulkDeleteOpen && (
        <Dialog open onOpenChange={(v) => { if (!v) setBulkDeleteOpen(false); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Remove {selectionCount} entries?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              This soft-deletes the selected entries. Engine matching ignores
              them immediately. They remain in the database (recoverable) but
              do not show in the portal list.
            </p>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={bulkDelete.isPending}>Cancel</Button>
              </DialogClose>
              <Button
                variant="destructive"
                onClick={() => bulkDelete.mutate()}
                disabled={bulkDelete.isPending}
              >
                {bulkDelete.isPending ? 'Removing…' : `Remove ${selectionCount}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {importOpen && (
        <ImportCsvDialog
          listId={listId}
          onClose={() => setImportOpen(false)}
          onSuccess={() => { invalidate(); }}
        />
      )}
    </div>
  );
}

// ── Single entry row ──────────────────────────────────────────────────────────

/**
 * Engine-eligibility classifier — mirrors the SQL fragment ENTRY_ELIGIBLE_NOW
 * in backend/src/authLists/service.ts. The four buckets we surface to the
 * portal are:
 *
 *   - 'active'    — passes every gate. Engine matches this entry right now.
 *   - 'scheduled' — active=true, start_date in the future. Will go live later.
 *   - 'expired'   — active=true, end_date already in the past.
 *   - 'disabled'  — active=false (regardless of dates).
 *
 * Distinct from `entry.active` alone — the date window can override the
 * active flag in either direction. Operator wants to know "is this row
 * counting RIGHT NOW?" without doing the math themselves.
 */
type EntryStatus = 'active' | 'scheduled' | 'expired' | 'disabled';

function classifyEntry(entry: AuthListEntryRow): EntryStatus {
  if (!entry.active) return 'disabled';
  const now = Date.now();
  if (entry.start_date && Date.parse(entry.start_date) > now) return 'scheduled';
  if (entry.end_date && Date.parse(entry.end_date) < now) return 'expired';
  return 'active';
}

function StatusChip({ status }: { status: EntryStatus }) {
  if (status === 'active') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-300 text-[10px]"
        title="Engine matches this entry right now"
      >
        <CheckCircle2 className="h-2.5 w-2.5" />
        Active
      </Badge>
    );
  }
  if (status === 'scheduled') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-300 text-[10px]"
        title="Start date is in the future — engine will start matching when the window opens"
      >
        <Clock className="h-2.5 w-2.5" />
        Scheduled
      </Badge>
    );
  }
  if (status === 'expired') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300 text-[10px]"
        title="End date has passed — engine no longer matches this entry"
      >
        <Clock className="h-2.5 w-2.5" />
        Expired
      </Badge>
    );
  }
  // disabled
  return (
    <Badge
      variant="outline"
      className="gap-1 border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300 text-[10px]"
      title="Active toggle is off — engine ignores this entry"
    >
      <XCircle className="h-2.5 w-2.5" />
      Disabled
    </Badge>
  );
}

function EntryRow({
  entry, listKind, checked, onCheckedChange, onToggleActive, onEdit, onDelete,
  canMutate, pendingActive,
}: {
  entry: AuthListEntryRow;
  listKind: AuthListKind;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  onToggleActive: (v: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  canMutate: boolean;
  pendingActive: boolean;
}) {
  // listKind isn't currently used for row rendering, but we accept it so the
  // edit dialog (popped via onEdit) doesn't have to look it up separately.
  void listKind;

  const status = classifyEntry(entry);
  const dim = status !== 'active';

  return (
    <tr className={dim ? 'opacity-60' : ''}>
      <td className="px-3 py-2">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onCheckedChange(Boolean(v))}
          aria-label="Select entry"
        />
      </td>
      <td className="px-3 py-2">
        <StatusChip status={status} />
      </td>
      <td className="px-3 py-2">
        {entry.e164 ? (
          <span className="inline-flex items-center gap-1.5 font-mono text-xs">
            <Phone className="h-3 w-3 text-slate-400" />
            {entry.e164}
          </span>
        ) : (
          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        {entry.label ? (
          <span className="text-xs">{entry.label}</span>
        ) : (
          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
        )}
      </td>
      <td className="px-3 py-2 max-w-[14rem]">
        {entry.description ? (
          <span
            className="text-xs text-muted-foreground line-clamp-2"
            title={entry.description}
          >
            {entry.description}
          </span>
        ) : (
          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        {entry.has_pin ? (
          <Badge variant="outline" className="text-[10px] gap-1">
            <Key className="h-2.5 w-2.5" />
            set
          </Badge>
        ) : (
          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        {/* The Status chip column already conveys "scheduled" / "expired" /
            "disabled" — this Switch just lets the operator flip active. */}
        <Switch
          checked={entry.active}
          disabled={!canMutate || pendingActive}
          onCheckedChange={onToggleActive}
          aria-label={entry.active ? 'Deactivate entry' : 'Activate entry'}
        />
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground font-mono">
        {entry.start_date
          ? entry.start_date.slice(0, 10)
          : <span className="text-slate-300 dark:text-slate-600">—</span>}
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground font-mono">
        {entry.end_date
          ? entry.end_date.slice(0, 10)
          : <span className="text-slate-300 dark:text-slate-600">—</span>}
      </td>
      <td className="px-3 py-2">
        {canMutate && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Row actions"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-3.5 w-3.5 me-2" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive focus:bg-red-50 dark:focus:bg-red-950/40"
                onClick={onDelete}
              >
                <Trash2 className="h-3.5 w-3.5 me-2" /> Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  );
}

// ── Add / Edit entry dialog (shared shape) ────────────────────────────────────

/**
 * One dialog handles both create and edit. The shape of the form is the same
 * — only the submit verb + initial values differ. Keeping them together
 * avoids the duplicated-validation bug we'd hit splitting them.
 *
 * datetime-local inputs return strings without timezone. We treat them as
 * local time, then convert to ISO (with offset) before sending. Empty
 * string = clear the field server-side (sent as null).
 */
function EntryFormDialog({
  mode, listId, listKind, customerId, entry, onClose,
}: {
  mode: 'create' | 'edit';
  listId: string;
  listKind: AuthListKind;
  /** Scopes the PhoneInput's default country prefix to the list's tenant. */
  customerId: string;
  entry?: AuthListEntryRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [e164, setE164] = useState(entry?.e164 ?? '');
  const [pin, setPin] = useState('');
  const [clearPin, setClearPin] = useState(false);
  const [label, setLabel] = useState(entry?.label ?? '');
  const [description, setDescription] = useState(entry?.description ?? '');
  const [active, setActive] = useState(entry?.active ?? true);
  // Date-only (YYYY-MM-DD) values for the <input type="date"> calendar
  // picker. Converted to ISO with 00:00:00 (start) / 23:59:59 (end) UTC
  // before sending so the engine's `end_date >= now()` check covers the
  // full calendar day.
  const [startDate, setStartDate] = useState(toDateInputValue(entry?.start_date));
  const [endDate, setEndDate] = useState(toDateInputValue(entry?.end_date));
  const [error, setError] = useState<string | null>(null);

  const isBlacklist = listKind === 'blacklist';
  const isPinList = listKind === 'pin_list';
  const isCidPinList = listKind === 'cid_pin_list';
  const showPhone = !isPinList || isCidPinList; // phone is required for CID-style lists
  const showPin = !isBlacklist;

  const e164Valid = e164.trim() === '' || E164_RE.test(e164.trim());
  const pinValid = pin === '' || PIN_RE.test(pin);
  const datesOk = !startDate || !endDate || new Date(startDate) <= new Date(endDate);
  const hasAtLeastOne = e164.trim() !== '' || pin !== '' || (mode === 'edit' && entry?.has_pin && !clearPin);
  const blacklistOk = !isBlacklist || e164.trim() !== '';
  const pinListOk = !isPinList || (mode === 'edit' ? entry?.has_pin || pin !== '' : pin !== '');
  const cidPinOk = !isCidPinList || (e164.trim() !== '' && (mode === 'edit' ? entry?.has_pin || pin !== '' : pin !== ''));
  const canSubmit =
    e164Valid && pinValid && datesOk && hasAtLeastOne &&
    blacklistOk && pinListOk && cidPinOk;

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        e164: e164.trim() || null,
        ...(mode === 'create'
          ? { pin: pin || null }
          : { pin: pin || undefined, clear_pin: clearPin || undefined }),
        label: label.trim() || null,
        description: description.trim() || null,
        active,
        start_date: startDate ? dateInputToStartIso(startDate) : null,
        end_date: endDate ? dateInputToEndIso(endDate) : null,
      };
      return mode === 'create'
        ? authLists.createEntry(listId, body)
        : authLists.updateEntry(listId, entry!.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['authListEntries', listId] });
      qc.invalidateQueries({ queryKey: ['authList', listId] });
      qc.invalidateQueries({ queryKey: ['authLists'] });
      toast({ title: mode === 'create' ? 'Entry added' : 'Entry updated' });
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Save failed.');
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add entry' : 'Edit entry'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {showPhone && (
            <div className="space-y-1">
              <Label htmlFor="ef-phone">Phone number</Label>
              <PhoneInput
                id="ef-phone"
                value={e164}
                onChange={setE164}
                customerId={customerId}
                placeholder="800 555 1234"
                inputClassName="text-sm"
              />
              {e164.trim() !== '' && !e164Valid && (
                <p className="text-xs text-destructive">Enter a valid phone number.</p>
              )}
            </div>
          )}
          {showPin && (
            <div className="space-y-1">
              <Label htmlFor="ef-pin">
                PIN {(isPinList || isCidPinList) ? '(required)' : '(optional)'}
              </Label>
              {mode === 'edit' && entry?.has_pin && !clearPin && (
                <button
                  type="button"
                  className="text-xs text-destructive underline underline-offset-2 hover:no-underline block"
                  onClick={() => setClearPin(true)}
                >
                  <X className="h-3 w-3 inline me-1" /> Clear existing PIN
                </button>
              )}
              {clearPin && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  PIN will be cleared.{' '}
                  <button type="button" className="underline" onClick={() => setClearPin(false)}>
                    Undo
                  </button>
                </p>
              )}
              {!clearPin && (
                <Input
                  id="ef-pin"
                  type="password"
                  autoComplete="off"
                  value={pin}
                  onChange={(ev) => setPin(ev.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder={mode === 'edit' && entry?.has_pin
                    ? 'Enter new PIN to rotate (leave blank to keep)'
                    : '3–8 digits'}
                  className="font-mono text-sm"
                />
              )}
              {pin !== '' && !pinValid && (
                <p className="text-xs text-destructive">Must be 3–8 digits.</p>
              )}
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="ef-label">Name (optional)</Label>
            <Input
              id="ef-label"
              value={label}
              onChange={(ev) => setLabel(ev.target.value)}
              placeholder="e.g. Alice Jones"
            />
            <p className="text-[11px] text-muted-foreground">
              Short one-liner shown in the entry list.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ef-desc">Description (optional)</Label>
            <Textarea
              id="ef-desc"
              value={description}
              onChange={(ev) => setDescription(ev.target.value)}
              rows={2}
              maxLength={2000}
              placeholder="Longer notes — context, why they're on the list, related ticket #, etc."
            />
          </div>
          <div className="flex items-center justify-between rounded border border-border px-3 py-2">
            <div>
              <div className="text-sm font-medium">Active</div>
              <div className="text-xs text-muted-foreground">
                Off = parked. The engine ignores it as if soft-deleted, but
                the row stays in the portal for later re-enable.
              </div>
            </div>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Start date (optional)</Label>
              {/* In-house DateTimePicker in date-only mode — the validity
                  window is a calendar DAY (server snaps to UTC start/end of
                  day), so no time sliders. State stays YYYY-MM-DD; the
                  helpers below convert to ISO before sending. */}
              <DateTimePicker
                dateOnly
                value={startDate}
                onChange={setStartDate}
                placeholder="Pick a start date"
                className="w-full justify-start"
              />
            </div>
            <div className="space-y-1">
              <Label>End date (optional)</Label>
              <DateTimePicker
                dateOnly
                value={endDate}
                onChange={setEndDate}
                placeholder="Pick an end date"
                className="w-full justify-start"
              />
            </div>
          </div>
          {!datesOk && (
            <p className="text-xs text-destructive">End must be on or after start.</p>
          )}
          {!hasAtLeastOne && (
            <p className="text-xs text-muted-foreground">Provide a phone, a PIN, or both.</p>
          )}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={mutation.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !canSubmit}
          >
            {mutation.isPending
              ? (mode === 'create' ? 'Adding…' : 'Saving…')
              : (mode === 'create' ? 'Add entry' : 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Confirm-remove ────────────────────────────────────────────────────────────

function ConfirmRemoveDialog({
  entry, onCancel, onConfirm, busy,
}: {
  entry: AuthListEntryRow;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Remove entry?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {entry.e164 ? <span className="font-mono">{entry.e164}</span> : 'This PIN-only entry'}
          {entry.label && <> (<span className="italic">{entry.label}</span>)</>}{' '}
          will be soft-removed.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? 'Removing…' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── CSV import dialog ─────────────────────────────────────────────────────────

/**
 * CSV bulk-import. The user picks a .csv file (we also accept .txt). We
 * read it as text on the client and POST the raw contents — no multipart
 * parser needed server-side.
 *
 * The sample CSV link inside the dialog is the same template the toolbar's
 * "Download sample template" exposes, mirrored here so it's discoverable
 * inside the import flow.
 */
function ImportCsvDialog({
  listId, onClose, onSuccess,
}: {
  listId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const importMutation = useMutation({
    mutationFn: () => authLists.bulkImportEntries(listId, csvText!),
    onSuccess: (data) => {
      setResult(data);
      onSuccess();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Import failed.');
    },
  });

  const onFile = async (file: File) => {
    setError(null);
    setResult(null);
    setFilename(file.name);
    const text = await file.text();
    setCsvText(text);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Import entries from CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            Pick a CSV with the columns:{' '}
            <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded">
              e164, pin, label, description, active, start_date, end_date
            </code>{' '}
            — &ldquo;label&rdquo; in the CSV is the entry&rsquo;s name. Only e164
            and label are typically filled; the rest are optional.
          </p>
          <button
            type="button"
            onClick={() => { void authLists.downloadSampleCsv(); }}
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
          >
            <Download className="h-3 w-3" /> Download sample template
          </button>

          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:text-slate-700 hover:file:bg-slate-200 dark:file:bg-slate-800 dark:file:text-slate-200"
          />

          {filename && csvText && (
            <p className="text-xs text-muted-foreground">
              Selected: <span className="font-mono">{filename}</span>
              {' '}({Math.round(csvText.length / 1024)} KB)
            </p>
          )}

          {result && (
            <div className="rounded border border-border bg-muted/30 p-3 text-sm space-y-2">
              <div>
                <span className="font-medium">{result.created}</span> created
                {' '}of {result.total} rows.
                {result.errors.length > 0 && (
                  <span className="text-amber-700 dark:text-amber-400">
                    {' '}{result.errors.length} failed.
                  </span>
                )}
              </div>
              {result.errors.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">
                    Show row errors
                  </summary>
                  <ul className="mt-1 max-h-40 overflow-auto pl-4 list-disc">
                    {result.errors.map((e) => (
                      <li key={`${e.row}-${e.reason}`}>
                        Row {e.row}: <code>{e.reason}</code>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={importMutation.isPending}>
              {result ? 'Close' : 'Cancel'}
            </Button>
          </DialogClose>
          {!result && (
            <Button
              onClick={() => importMutation.mutate()}
              disabled={!csvText || importMutation.isPending}
            >
              {importMutation.isPending ? 'Importing…' : 'Import'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Date helpers ──────────────────────────────────────────────────────────────
//
// `<input type="date">` uses YYYY-MM-DD (date-only). The server stores
// ISO 8601 with full timestamp. Conversion rules:
//
//   - Display:  ISO  → take the date portion in UTC (slice 0..10).
//                     This way "2026-01-15T23:59:59Z" displays as
//                     "2026-01-15" in the input regardless of the
//                     viewer's timezone — matches the day they picked.
//   - Send (start_date): "YYYY-MM-DD" → 00:00:00 UTC of that day.
//   - Send (end_date):   "YYYY-MM-DD" → 23:59:59.999 UTC of that day so
//                     the engine's `end_date >= now()` filter keeps the
//                     entry valid for the WHOLE day the operator picked.

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  // ISO strings start with YYYY-MM-DD — slice the first 10 chars to get
  // the calendar date. Done in UTC because the server-side semantics
  // treat the date as UTC midnight (start) / end-of-day (end), so
  // viewing the same date back is the right thing.
  return iso.slice(0, 10);
}

function dateInputToStartIso(date: string): string | null {
  if (!date) return null;
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function dateInputToEndIso(date: string): string | null {
  if (!date) return null;
  // 23:59:59.999 of that calendar day so the entry remains eligible
  // for the entire day under the engine's `end_date >= now()` check.
  const d = new Date(`${date}T23:59:59.999Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
